<template>
  <section v-if="trail" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
    <h2 class="text-2xl font-bold mb-4 text-foreground">{{ $t('featuredTrail.heading') }}</h2>

    <div
      class="bg-card rounded-xl border border-border shadow-sm overflow-hidden lg:flex lg:items-stretch"
    >
      <router-link v-if="heroImageUrl" :to="trailRoute" class="block lg:w-1/2 lg:shrink-0">
        <img
          :src="heroImageUrl"
          :alt="heroImageAlt"
          class="w-full aspect-video object-cover bg-muted"
          loading="lazy"
        />
      </router-link>

      <div class="p-5 flex-1">
        <router-link :to="trailRoute" class="inline-block">
          <h3
            class="text-xl lg:text-2xl font-bold text-primary hover:text-primary/80 transition-colors"
          >
            {{ trail.name }}
          </h3>
        </router-link>
        <p
          v-if="trail.primary_zone || displayedTypes.length > 0"
          class="text-muted-foreground text-sm mt-1"
        >
          <span v-if="trail.primary_zone">{{ trail.primary_zone.name }}</span>
          <span v-if="trail.primary_zone && displayedTypes.length > 0" class="mx-2">·</span>
          <span v-if="displayedTypes.length > 0">{{ trailTypesLabel }}</span>
        </p>

        <div class="flex flex-wrap gap-3 mt-3 text-sm text-muted-foreground">
          <span v-if="distanceLabel" class="inline-flex items-center gap-1">
            <Ruler class="w-4 h-4" />
            <span class="font-medium text-foreground">{{ distanceLabel }}</span>
          </span>
          <span v-if="elevationLabel" class="inline-flex items-center gap-1">
            <span class="font-medium text-foreground">↑ {{ elevationLabel }}</span>
          </span>
          <span v-if="durationLabel" class="inline-flex items-center gap-1">
            <Clock class="w-4 h-4" />
            <span class="font-medium text-foreground">{{ durationLabel }}</span>
          </span>
        </div>

        <p v-if="attributionUsername" class="text-sm text-muted-foreground mt-3">
          <template v-if="showAttributionLink">
            <router-link
              :to="{ path: `/u/${attributionUsername}` }"
              class="hover:text-primary transition-colors"
            >
              {{ $t('featuredTrail.by', { handle: `@${attributionUsername}` }) }}
            </router-link>
          </template>
          <template v-else>
            {{ $t('featuredTrail.by', { handle: `@${attributionUsername}` }) }}
          </template>
        </p>

        <div class="mt-4">
          <router-link
            :to="trailRoute"
            class="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors"
          >
            {{ $t('featuredTrail.viewTrail') }}
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Clock, Ruler } from 'lucide-vue-next'
import { useLocalePath } from '../composables/useLocalePath'
import { formatDistance } from '../utils/formatDistance'
import { useLocalizedName } from '../utils/localizedName'
import featuredTrailApi from '../api/featuredTrail'

const { localePath } = useLocalePath()
const localizedName = useLocalizedName()

const trail = ref(null)

const TYPE_CAP = 3

const displayedTypes = computed(() => (trail.value?.trail_types || []).slice(0, TYPE_CAP))

const trailTypesLabel = computed(() => displayedTypes.value.map(localizedName).join(', '))

const heroImageUrl = computed(() => {
  if (trail.value) {
    return trail.value.photo?.url || trail.value.map_snapshot_hero_url || null
  }
  return null
})

const heroImageAlt = computed(() => trail.value?.photo?.alt || trail.value?.name || '')

const distanceLabel = computed(() => {
  const m = trail.value?.stats?.distance_m
  return m == null ? null : formatDistance(m)
})

const elevationLabel = computed(() => {
  const m = trail.value?.stats?.elevation_gain_m
  return m == null ? null : formatDistance(m)
})

const durationLabel = computed(() => {
  // Descent-only trails carry no trustworthy duration estimate (#411).
  if (trail.value?.descent_only) return null
  const stats = trail.value?.stats
  if (!stats) return null
  const first = stats.estimated_durations && Object.values(stats.estimated_durations)[0]
  const minutes = first ?? stats.estimated_duration_minutes
  if (minutes == null) return null
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return hours > 0 ? `${hours}h ${mins} min` : `${mins} min`
})

const attributionUsername = computed(() => trail.value?.user?.username || null)

const showAttributionLink = computed(
  () => !!trail.value?.user?.profile_enabled && !!attributionUsername.value,
)

const trailRoute = computed(() => {
  if (!trail.value || !trail.value.primary_zone) return { path: '/' }
  return localePath('Trail', {
    zone_slug: trail.value.primary_zone.slug,
    slug: trail.value.slug,
  })
})

const fetchFeatured = async () => {
  try {
    const response = await featuredTrailApi.get()
    trail.value = response && response.status !== 204 && response.data ? response.data : null
  } catch (error) {
    console.error('Error fetching featured trail:', error)
    trail.value = null
  }
}

onMounted(() => {
  fetchFeatured()
})
</script>
